// @flow
import * as React from 'react';
import DOMPurify from 'dompurify';
import './message-item.css';
import AiProductsTable from './ai-products-table';
import Icon from '../../basic-components/icon';
import t from './locale';

type Message = {
  id?: string,
  type: 'user' | 'bot',
  author: string,
  text: string,
  time: string,
  state?: string,
  suggestedActions?: Array<string>,
  products?: Array<any>,
  publishUrl?: string,
  analysisReport?: string,
  executionInfo?: any,
  errorInfo?: any,
  intakeInfo?: any,
  messageId?: string,
  feedbackMessage?: ?{
    messageId: ?string,
    isHelpful: ?boolean,
    feedbackMessage: ?string,
    feedbackTime: ?string,
  },
};

type Props = {
  message: Message,
  queryParams?: ?{ [string]: string },
  onSuggestedAction?: (action: string) => void,
  onHelpfulClick?: (messageId: string) => void,
  onNotHelpfulClick?: (messageId: string) => void,
  onPublishClick?: (queryParams: ?{ [string]: string }) => void,
  isSubmitting?: boolean,
};

/**
 * Enhanced markdown renderer matching Vue version
 */
const renderMarkdown = (text) => {
  if (!text) return '';

  let html = text
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // List items
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
    // Paragraphs and line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  // Wrap paragraphs
  html = `<p>${html}</p>`;

  // Wrap lists
  html = html.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>');

  return html;
};

/**
 * Get state label
 */
const getStateLabel = (state) => {
  const labels = {
    INTAKE: t('stateCollectingInfo'),
    COMPLETED: t('stateCompleted'),
    BLOCKED: t('stateBlocked'),
    ERROR: t('stateError'),
  };
  return labels[state] || state;
};

/**
 * Renders a single chat message with full metadata support
 */
function MessageItem({
  message,
  queryParams,
  onSuggestedAction, // eslint-disable-line no-unused-vars
  onHelpfulClick,
  onNotHelpfulClick,
  onPublishClick,
  isSubmitting,
}: Props): React.Node {
  const {
    id,
    type,
    text,
    time,
    state,
    products,
    publishUrl,
    analysisReport,
    intakeInfo,
    feedbackMessage,
  } = message;

  const getInitialFeedback = () => {
    if (feedbackMessage?.isHelpful === true) return 'helpful';
    if (feedbackMessage?.isHelpful === false) return 'not-helpful';
    return null;
  };

  const [feedback, setFeedback] = React.useState<
    'helpful' | 'not-helpful' | null
  >(getInitialFeedback());

  // Sync feedback state when feedbackMessage prop changes (e.g. after history reload)
  React.useEffect(() => {
    setFeedback(getInitialFeedback());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackMessage?.isHelpful]);

  return (
    <div className={`ai-chat-message ai-chat-${type}`}>
      <div className="ai-chat-message-content">
        {/* Time display with bot info for bot messages */}
        {type === 'bot' ? (
          <>
            <div className="ai-chat-time-header">
              <div className="ai-chat-bot-info">
                <Icon name="ebayAi" className="ai-chat-bot-icon" />
                <span className="ai-chat-bot-name">{t('assistantName')}</span>
              </div>
              <span className="ai-chat-time">{time}</span>
              {id !== 'welcome' && (
                <div className="ai-chat-curated-label">
                  <span className="ai-chat-curated-text">Curated with eBay.ai</span>
                  <Icon name="ebayAi" className="ai-chat-curated-icon" />
                </div>
              )}
            </div>
            <div className="ai-chat-content-container">
              {/* Main message text with error badge if needed */}
              <div className="ai-chat-message-text">
                {state === 'ERROR' && (
                  <span className="ai-chat-error-badge">
                    {getStateLabel('ERROR')}
                  </span>
                )}
                {text}
                {publishUrl && (
                  <a
                    href={publishUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ai-chat-publish-link"
                    onClick={() => onPublishClick && onPublishClick(queryParams)}
                  >
                    {t('clickHereToJump')}
                  </a>
                )}
              </div>

              {/* Analysis report */}
              {analysisReport && (
                <div
                  className="ai-chat-analysis-report"
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(renderMarkdown(analysisReport)),
                  }}
                />
              )}

              {/* Products section - uses the new full products table component */}
              {queryParams && products && products.length > 0 && (
                <AiProductsTable
                  products={products}
                  queryParams={queryParams}
                />
              )}

              {/* INTAKE info */}
              {intakeInfo && (
                <div className="ai-chat-intake-info">
                  <div className="ai-chat-required-info">
                    {intakeInfo.requiredInfo}
                  </div>
                  {intakeInfo.suggestedQuestions &&
                    intakeInfo.suggestedQuestions.length > 0 && (
                      <div className="ai-chat-suggested-questions">
                        <div className="ai-chat-questions-title">
                          {t('suggestedQuestions')}
                        </div>
                        <ul>
                          {intakeInfo.suggestedQuestions.map((q) => (
                            <li key={q}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              )}

              {/* Feedback section - only for bot messages, exclude welcome and error messages */}
              {id !== 'welcome' && state !== 'ERROR' && (
                <div className="ai-chat-feedback">
                  <span className="ai-chat-feedback-text">
                    {t('feedbackQuestion')}
                  </span>
                  <div className="ai-chat-feedback-buttons">
                    <button
                      type="button"
                      className={`ai-chat-feedback-button ${
                        feedback === 'helpful' ? 'active' : ''
                      }`}
                      onClick={() => onHelpfulClick && onHelpfulClick(id)}
                      disabled={feedback !== null || isSubmitting}
                    >
                      {t('feedbackHelpful')}
                    </button>
                    <button
                      type="button"
                      className={`ai-chat-feedback-button ${
                        feedback === 'not-helpful' ? 'active' : ''
                      }`}
                      onClick={() => onNotHelpfulClick && onNotHelpfulClick(id)}
                      disabled={feedback !== null || isSubmitting}
                    >
                      {t('feedbackNotHelpful')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <span className="ai-chat-time">{time}</span>
            {/* Main message text with error badge if needed */}
            <div className="ai-chat-message-text">
              {state === 'ERROR' && (
                <span className="ai-chat-error-badge">
                  {getStateLabel('ERROR')}
                </span>
              )}
              {text}
              {publishUrl && (
                <a
                  href={publishUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ai-chat-publish-link"
                  onClick={() => onPublishClick && onPublishClick(queryParams)}
                >
                  {t('clickHereToJump')}
                </a>
              )}
            </div>

            {/* Analysis report */}
            {analysisReport && (
              <div
                className="ai-chat-analysis-report"
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(renderMarkdown(analysisReport)),
                }}
              />
            )}

            {/* Products section - uses the new full products table component */}
            {queryParams && (
              <AiProductsTable
                products={products || []}
                queryParams={queryParams}
              />
            )}

            {/* INTAKE info */}
            {intakeInfo && (
              <div className="ai-chat-intake-info">
                <div className="ai-chat-required-info">
                  {intakeInfo.requiredInfo}
                </div>
                {intakeInfo.suggestedQuestions &&
                  intakeInfo.suggestedQuestions.length > 0 && (
                    <div className="ai-chat-suggested-questions">
                      <div className="ai-chat-questions-title">
                        {t('suggestedQuestions')}
                      </div>
                      <ul>
                        {intakeInfo.suggestedQuestions.map((q) => (
                          <li key={q}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MessageItem;
